import LegalDocument, { LegalList, LegalSection } from "../components/LegalDocument";
import { brandIdentity } from "../data/brand";

const officialResources = [
  ["Morocco CNDP and Law No. 09-08", "https://www.cndp.ma/loi-09-08/"],
  ["European Commission data-protection overview", "https://commission.europa.eu/law/law-topic/data-protection_en"],
  ["Google advertising privacy and consent information", "https://support.google.com/adsense/answer/7670013?hl=en"],
  ["X for Websites privacy information", "https://help.x.com/en/x-for-websites-ads-info-and-privacy"],
];

export default function PrivacyPolicy() {
  return (
    <LegalDocument
      path="/privacy-policy"
      title="Privacy Policy"
      seoTitle="Privacy Policy TechniaHQRobot"
      description="Read how TechniaHQRobot handles contact messages, technical logs, browser storage, advertising services, embedded content and privacy requests."
      eyebrow="Privacy and personal data"
      updatedAt="August 4, 2026"
      schemaType="WebPage"
    >
      <LegalSection id="scope" title="1. Who operates this website">
        <p>
          TechniaHQRobot is an independent robotics media website available at <strong>{brandIdentity.baseUrl}</strong>. This policy explains what information may be processed when you browse the site, use the contact page, open embedded content or interact with advertising services.
        </p>
        <p>
          Privacy questions and requests can be sent to <a className="font-black text-blue-700 underline underline-offset-4" href={`mailto:${brandIdentity.email}`}>{brandIdentity.email}</a>. No postal address or company-registration details are published on the site, so this policy does not invent them.
        </p>
      </LegalSection>

      <LegalSection id="data" title="2. Information that may be processed">
        <LegalList>
          <li><strong>Contact information</strong> your name, email address, selected topic and message when you prepare an email through the contact form.</li>
          <li><strong>Technical records</strong> IP address, browser type, device information, requested URL, referring page, timestamps and security events that hosting, delivery or security providers may record.</li>
          <li><strong>Browser storage</strong> cookies, local storage and session storage used to remember whether a social prompt was shown, control advertising frequency, store consent choices where enabled and keep the site stable.</li>
          <li><strong>Advertising data</strong> device identifiers, approximate location, ad interactions and related signals that Google AdSense may process under its own terms and privacy notices.</li>
          <li><strong>Embedded-content data</strong> information sent to X or another media platform when an embedded post, player or external widget loads or when you interact with it.</li>
        </LegalList>
      </LegalSection>

      <LegalSection id="contact-form" title="3. How the contact form works">
        <p>
          The contact form does not submit your message to a TechniaHQRobot database. It validates the fields in your browser and opens a new email draft addressed to {brandIdentity.email}. Your email provider and device then handle the message under their own privacy rules.
        </p>
        <p>
          Messages received by email may be retained for as long as reasonably necessary to answer the request, document a business discussion, handle a correction or copyright issue, prevent abuse, or meet a legal obligation.
        </p>
      </LegalSection>

      <LegalSection id="purposes" title="4. Why information is used">
        <LegalList>
          <li>Deliver pages, media and site functions.</li>
          <li>Protect the website against spam, fraud, malicious traffic and technical failures.</li>
          <li>Respond to editorial tips, corrections, partnership requests, advertising questions and support messages.</li>
          <li>Measure content performance and control advertising frequency where those tools are enabled.</li>
          <li>Comply with applicable legal duties and enforce the Terms of Use.</li>
        </LegalList>
        <p>
          Depending on the visitor and jurisdiction, processing may rely on consent, performance of a requested service, legitimate interests in operating and securing the site, or compliance with law. Consent can be withdrawn for future processing where consent is the applicable basis.
        </p>
      </LegalSection>

      <LegalSection id="services" title="5. Services and third parties">
        <p>
          TechniaHQRobot uses or may use Firebase Hosting, Google AdSense, Google Fonts, X embeds and links to social platforms. These providers may receive technical information directly from your browser. Their processing, retention periods and international transfers are controlled by their own policies.
        </p>
        <p>
          TechniaHQRobot does not claim to sell visitors’ personal information. Information may still be disclosed to service providers, professional advisers or authorities when necessary to operate the website, investigate abuse, protect rights or comply with a valid legal request.
        </p>
      </LegalSection>

      <LegalSection id="retention" title="6. Retention and security">
        <p>
          Information is kept only for the period reasonably needed for the purpose described above. Exact hosting, advertising and platform retention periods are set by the relevant providers and can change. Reasonable technical and organizational measures are used, but no website or email system can guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection id="rights" title="7. Your privacy rights">
        <p>
          Depending on where you live and which law applies, you may have rights to request access, correction, deletion, restriction, portability or objection, and to withdraw consent. Morocco’s Law No. 09-08 includes rights relating to access, correction and opposition. The GDPR provides additional rights where it applies.
        </p>
        <p>
          Send a clear request to <a className="font-black text-blue-700 underline underline-offset-4" href={`mailto:${brandIdentity.email}?subject=Privacy%20request`}>{brandIdentity.email}</a>. Include enough detail to identify the relevant correspondence or activity. Identity verification may be requested before disclosing or changing personal information.
        </p>
      </LegalSection>

      <LegalSection id="children" title="8. Children">
        <p>
          The website publishes general robotics and technology information and is not designed to collect personal information from children. A parent or guardian who believes a child submitted personal information can request its review or deletion by email.
        </p>
      </LegalSection>

      <LegalSection id="external" title="9. Official privacy resources">
        <ul className="grid gap-3">
          {officialResources.map(([label, href]) => (
            <li key={href}>
              <a className="font-black text-blue-700 underline underline-offset-4" href={href} target="_blank" rel="noopener noreferrer">{label}</a>
            </li>
          ))}
        </ul>
      </LegalSection>

      <LegalSection id="updates" title="10. Policy updates">
        <p>
          This policy may be revised when the site adds a service, changes an advertising provider or receives new legal guidance. The date at the top identifies the current version. Material changes will be presented on this page before or when they take effect where reasonably possible.
        </p>
      </LegalSection>
    </LegalDocument>
  );
}
